import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient } from '@wix/sdk-types';
import * as ambassadorWixFunctionsV1EditorFunction from './functions-v1-editor-function-editor-functions.http.js';

/** An EditorFunction is an entity storing data needed to support Functions of predefined Function types inside Editor and supporting Editor lifecycle. */
export interface EditorFunction {
  /**
   * EditorFunction ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
}

/** Invoke Function Request */
export interface InvokeEditorFunctionRequest {
  /**
   * Id of the function requested to be invoked
   * @format GUID
   */
  functionId: string | null;
}

/** Invoke Function Response */
export interface InvokeEditorFunctionResponse {
  /** The result data returned by the executed Editor Function. Structure depends on the specific function implementation. */
  output?: Record<string, any> | null;
}

/** @docsIgnore */
export type InvokeEditorFunctionApplicationErrors = {
  code?: 'NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Invokes the specified Editor Function. Returns a NOT_FOUND error if editor function can't be found by the provided id.
 * @param functionId - Id of the function requested to be invoked
 * @public
 * @documentationMaturity preview
 * @requiredField functionId
 * @permissionId functions:v1:editor_function:invoke_editor_function
 * @returns Invoke Function Response
 * @fqn wix.functions.editorfunctions.v1.EditorFunctions.InvokeEditorFunction
 */
export async function invokeEditorFunction(functionId: string): Promise<
  InvokeEditorFunctionResponse & {
    __applicationErrorsType?: InvokeEditorFunctionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    functionId: functionId,
  });

  const reqOpts =
    ambassadorWixFunctionsV1EditorFunction.invokeEditorFunction(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { functionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['functionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
